/**
 * InviteModal Component
 *
 * Modal dialog for sharing game invitation URLs.
 * Displays shareable URL and provides copy-to-clipboard functionality.
 *
 * Props:
 * - isOpen: boolean - Controls modal visibility
 * - onClose: function - Callback when modal is closed
 * - roomId: string - Unique room identifier for the game session
 */

import { useNavigate } from 'react-router-dom';
import { COPY } from '../copy';
import { useClipboard } from '../hooks/useClipboard';

export default function InviteModal({ isOpen, onClose, roomId }) {
  const navigate = useNavigate();
  const { copyToClipboard, isCopied } = useClipboard();

  if (!isOpen) return null;

  // Construct full shareable URL
  const shareableUrl = `${window.location.origin}/game/${roomId || ''}`;

  const handleCopy = async () => {
    await copyToClipboard(shareableUrl);
  };

  const handleStartGame = () => {
    navigate(`/game/${roomId}`);
    onClose();
  };

  const handleOverlayClick = (event) => {
    // Only close if clicking the overlay itself, not the modal content
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
      onClick={handleOverlayClick}
      data-testid="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-modal-title"
    >
      <div className="bg-dark-charcoal border-2 border-neon-cyan shadow-glow-cyan rounded-lg p-6 max-w-md w-full mx-4">
        <div className="mb-4">
          <h2
            id="invite-modal-title"
            className="text-2xl font-bold text-neon-cyan text-glow-cyan mb-2"
          >
            {COPY.modal.invite.title}
          </h2>
          <p className="text-gray-300 text-sm">
            {COPY.modal.invite.instructions}
          </p>
        </div>

        <div className="mb-6">
          <div className="bg-dark-purple border border-neon-cyan/30 rounded-lg p-3 mb-3">
            <p className="text-sm text-gray-200 break-all font-mono">
              {shareableUrl}
            </p>
          </div>

          <button
            onClick={handleCopy}
            className={`w-full py-2 px-4 rounded-lg font-medium transition-all border-2 ${
              isCopied
                ? 'bg-neon-green border-white/70 text-dark-navy shadow-glow-green'
                : 'bg-neon-cyan border-white/70 text-dark-navy hover:bg-neon-pink hover:border-white/70 hover:text-white shadow-glow-cyan'
            }`}
            aria-label={isCopied ? COPY.modal.invite.copyButtonCopied : COPY.modal.invite.copyButton}
          >
            {isCopied ? COPY.modal.invite.copyButtonCopied : COPY.modal.invite.copyButton}
          </button>
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-300 hover:text-neon-cyan font-medium transition-colors"
            aria-label={COPY.modal.invite.close}
          >
            {COPY.modal.invite.close}
          </button>
        </div>
      </div>
    </div>
  );
}
